import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  acceptIntercomConsent,
  declineIntercomConsent,
  hasDecidedIntercomConsent,
  isIntercomConfigured,
  subscribeIntercomConsent,
} from '@/lib/utils/intercom';
import { isImpersonating } from '@/lib/utils/managementAccess';
import {
  isOnboardingTourActive,
  subscribeOnboardingTourActive,
} from '@/lib/utils/onboardingTourActive';
import { useSessionStore } from '@/store/sessionStore';

// A just-arrived user's onboarding tour arms a beat after the session loads — it
// waits a frame for its anchor to mount before it takes the screen. Hold the
// banner for that beat so it never flashes in during the frames before the tour
// opens. One tour interval (300ms) plus margin covers the shell tour, whose
// header/rail anchor is present immediately; a tour that arms later still hides
// the banner the moment it opens, via `tourActive`.
const TOUR_SETTLE_MS = 400;

interface UseIntercomConsentBanner {
  /** Show the prompt only for a signed-in, non-impersonated user who hasn't
   *  decided yet. */
  visible: boolean;
  /** Grant consent (boots + identifies Intercom), then hide for good. */
  accept: () => void;
  /** Record the decline (no boot), then hide for good. */
  decline: () => void;
}

/**
 * Drives the app-level Intercom cookie-consent banner.
 *
 * The prompt is decoupled from the onboarding tour and the "?" help button:
 * every signed-in user who has not yet accepted or declined is asked once. Both
 * outcomes persist via `intercom.ts`, so the banner never reappears after a
 * choice — a decliner is not re-nagged on the next reload.
 *
 * Impersonated sessions are excluded: consent belongs to the human at the
 * keyboard, and while impersonating that is the operator, not the customer whose
 * session is on screen. See the `visible` computation below.
 */
export function useIntercomConsentBanner(): UseIntercomConsentBanner {
  const user = useSessionStore(s => s.user);
  // Consent can be decided from the "?" help dialog too, which writes the same
  // key — so subscribe rather than read once, or an outside Accept leaves this
  // banner stale (and a Decline on that stale banner would revoke it). `decided`
  // is true once either surface has chosen, either way.
  const storedDecision = useSyncExternalStore(
    subscribeIntercomConsent,
    hasDecidedIntercomConsent,
    () => true,
  );
  // Belt-and-suspenders for private mode: if the write above can't persist,
  // `hasDecidedIntercomConsent` still reads undecided, so latch this session's
  // own choice locally too. OR'd in below.
  const [locallyDecided, setLocallyDecided] = useState(false);
  const decided = storedDecision || locallyDecided;
  // The onboarding tour dims the page and anchors its own card; showing a second
  // card underneath reads as a collision. Yield while a tour step is on screen —
  // the banner reappears the moment the tour is finished or skipped. Users who
  // never get a tour are unaffected: the flag simply never flips.
  const tourActive = useSyncExternalStore(
    subscribeOnboardingTourActive,
    isOnboardingTourActive,
    () => false,
  );

  // Settle window (see `TOUR_SETTLE_MS`). Keyed to the user id, not mount: login
  // happens inside this same SPA with no remount, so a mount-time timer would
  // already be spent by the time the dashboard (and its tour) loads. Keying on
  // the id re-arms it exactly when a session appears, yet leaves it untouched on
  // plain navigation — so the banner never blinks as you move between pages.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    setSettled(false);
    const timer = setTimeout(() => setSettled(true), TOUR_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [user?.id]);

  const accept = useCallback(() => {
    acceptIntercomConsent(user);
    setLocallyDecided(true);
  }, [user]);

  const decline = useCallback(() => {
    declineIntercomConsent();
    setLocallyDecided(true);
  }, []);

  // No Intercom configured (E2E, or any env without an app id) → nothing to
  // consent to, so never show the prompt. Hold back while an onboarding tour owns
  // the screen (`tourActive`) and during the post-login settle window (`settled`).
  //
  // Never while impersonating. The customer on screen is not the human at the
  // keyboard, so the prompt would reach the wrong audience: an operator would
  // answer a consent question posed to the customer, and either answer persists
  // to the *operator's* browser — silencing the prompt for them for good while
  // the customer, whose activation is the point of this prompt, is still never
  // asked. Accept is worse than a wasted click: it boots the messenger mid-
  // impersonation, so a support conversation can open from inside a borrowed
  // session. `/management` is already excluded at the render site, but
  // impersonation leaves those routes — it lands on `/dashboard`, which is
  // exactly where this banner fires. `isImpersonating` reads both signals
  // (`impersonateId` + `unImpersonatedUser`) since either can arrive first.
  const visible =
    Boolean(user) &&
    !isImpersonating(user) &&
    !decided &&
    isIntercomConfigured() &&
    !tourActive &&
    settled;
  return { visible, accept, decline };
}
